import React, { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Instagram,
  Facebook,
  Linkedin,
  Video,
  Image as ImageIcon,
  Calendar as CalendarIcon,
  Clock,
  Send,
  Save,
  X,
  Plus,
  Loader2,
  AlertCircle,
  CheckCircle2
} from 'lucide-react';
import { format, addHours, setHours, setMinutes } from 'date-fns';
import { SocialAccount, SocialPlatform, SocialPost, PLATFORM_INFO, CreateSocialPostRequest } from '@/types/social';
import { cn } from '@/lib/core/utils';
import { toast } from 'sonner';
import { PostPreview } from './PostPreview';
import { MediaUploader } from './MediaUploader';

interface PostComposerProps {
  businessId: string;
  accounts: SocialAccount[];
  onSubmit: (request: CreateSocialPostRequest) => Promise<void>;
  onCancel?: () => void;
  editingPost?: SocialPost | null;
  initialScheduleDate?: Date | null;
}

export const PostComposer: React.FC<PostComposerProps> = ({
  businessId,
  accounts,
  onSubmit,
  onCancel,
  editingPost,
  initialScheduleDate
}) => {
  // Determine initial schedule date: editing post > calendar click > undefined
  const getInitialScheduleDate = (): Date | undefined => {
    if (editingPost?.scheduled_for) {
      return new Date(editingPost.scheduled_for);
    }
    if (initialScheduleDate) {
      return initialScheduleDate;
    }
    return undefined;
  };

  const [content, setContent] = useState(editingPost?.content || '');
  const [mediaUrls, setMediaUrls] = useState<string[]>(editingPost?.media_urls || []);
  const [mediaTypes, setMediaTypes] = useState<string[]>(editingPost?.media_types || []);
  const [selectedPlatforms, setSelectedPlatforms] = useState<SocialPlatform[]>(
    editingPost?.target_platforms || []
  );
  const [scheduleDate, setScheduleDate] = useState<Date | undefined>(getInitialScheduleDate());
  const [scheduleTime, setScheduleTime] = useState<string>(
    editingPost?.scheduled_for
      ? format(new Date(editingPost.scheduled_for), 'HH:mm')
      : format(addHours(new Date(), 1), 'HH:00')
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewPlatform, setPreviewPlatform] = useState<SocialPlatform>('instagram');

  // Get active accounts by platform
  const activeAccounts = accounts.filter(a => a.status === 'active');
  const availablePlatforms = [...new Set(activeAccounts.map(a => a.platform))];

  const getPlatformIcon = (platform: SocialPlatform) => {
    switch (platform) {
      case 'instagram': return Instagram;
      case 'facebook': return Facebook;
      case 'linkedin': return Linkedin;
      case 'tiktok': return Video;
    }
  };

  const togglePlatform = (platform: SocialPlatform) => {
    setSelectedPlatforms(prev =>
      prev.includes(platform)
        ? prev.filter(p => p !== platform)
        : [...prev, platform]
    );
  };

  const getCharacterCount = () => {
    const counts: Record<SocialPlatform, { current: number; max: number; valid: boolean }> = {} as any;

    selectedPlatforms.forEach(platform => {
      const max = PLATFORM_INFO[platform].maxContentLength;
      const current = content.length;
      counts[platform] = {
        current,
        max,
        valid: current <= max
      };
    });

    return counts;
  };

  const characterCounts = getCharacterCount();
  const hasInvalidLength = Object.values(characterCounts).some(c => !c.valid);

  // Platform-specific validation
  const getPlatformValidation = () => {
    const warnings: string[] = [];
    const errors: string[] = [];

    if (selectedPlatforms.includes('tiktok')) {
      const hasVideo = mediaTypes.includes('video');
      if (!hasVideo) {
        errors.push('TikTok requires video content');
      }
      if (mediaUrls.length > 1) {
        warnings.push('TikTok only supports a single video');
      }
    }

    if (selectedPlatforms.includes('instagram')) {
      if (mediaUrls.length === 0) {
        errors.push('Instagram requires at least one image or video');
      }
    }

    if (selectedPlatforms.includes('linkedin')) {
      const hasVideo = mediaTypes.includes('video');
      if (hasVideo) {
        warnings.push('LinkedIn video publishing requires additional setup');
      }
    }

    return { warnings, errors };
  };

  const validation = getPlatformValidation();
  const hasValidationErrors = validation.errors.length > 0;

  const handleMediaAdd = (url: string, type: 'image' | 'video') => {
    setMediaUrls(prev => [...prev, url]);
    setMediaTypes(prev => [...prev, type]);
  };

  const handleMediaRemove = (index: number) => {
    setMediaUrls(prev => prev.filter((_, i) => i !== index));
    setMediaTypes(prev => prev.filter((_, i) => i !== index));
  };

  const getScheduledDateTime = (): string | undefined => {
    if (!scheduleDate) return undefined;

    const [hours, minutes] = scheduleTime.split(':').map(Number);
    const dateTime = setMinutes(setHours(scheduleDate, hours), minutes);
    return dateTime.toISOString();
  };

  const handleSubmit = async (publishNow: boolean = false) => {
    if (!content.trim()) {
      toast.error('Content required', { description: 'Please enter some content for your post.' });
      return;
    }

    if (selectedPlatforms.length === 0) {
      toast.error('Platform required', { description: 'Please select at least one platform.' });
      return;
    }

    if (hasInvalidLength) {
      toast.error('Content too long', { description: 'Your content exceeds the character limit for one or more platforms.' });
      return;
    }

    if (hasValidationErrors) {
      toast.error('Platform requirements not met', { description: validation.errors.join('. ') });
      return;
    }

    if (!publishNow && !scheduleDate) {
      toast.error('Schedule required', { description: 'Please select a date and time to schedule your post.' });
      return;
    }

    setIsSubmitting(true);

    try {
      await onSubmit({
        business_id: businessId,
        content: content.trim(),
        media_urls: mediaUrls,
        media_types: mediaTypes,
        target_platforms: selectedPlatforms,
        scheduled_for: publishNow ? undefined : getScheduledDateTime(),
        publish_now: publishNow,
      });

      toast.success(publishNow ? 'Post published!' : 'Post scheduled!', {
        description: publishNow
          ? 'Your post has been published to the selected platforms.'
          : `Your post will be published on ${format(new Date(getScheduledDateTime()!), 'PPP')} at ${scheduleTime}.`
      });

      // Reset form
      setContent('');
      setMediaUrls([]);
      setMediaTypes([]);
      setSelectedPlatforms([]);
      setScheduleDate(undefined);

      if (onCancel) onCancel();
    } catch (error) {
      console.error('Failed to submit post:', error);
      toast.error('Failed to submit', {
        description: 'There was an error submitting your post. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const timeOptions = Array.from({ length: 48 }, (_, i) => {
    const hour = Math.floor(i / 2);
    const minute = i % 2 === 0 ? '00' : '30';
    return `${hour.toString().padStart(2, '0')}:${minute}`;
  });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>{editingPost ? 'Edit Post' : 'Create New Post'}</span>
            {onCancel && (
              <Button variant="ghost" size="icon" onClick={onCancel}>
                <X className="h-4 w-4" />
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Platform Selection */}
          <div className="space-y-3">
            <Label>Post to</Label>
            {availablePlatforms.length === 0 ? (
              <div className="flex items-center gap-2 p-3 bg-yellow-50 text-yellow-800 rounded-md">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm">No connected accounts. Connect a social media account first.</span>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {availablePlatforms.map(platform => {
                  const Icon = getPlatformIcon(platform);
                  const info = PLATFORM_INFO[platform];
                  const isSelected = selectedPlatforms.includes(platform);
                  const account = activeAccounts.find(a => a.platform === platform);

                  return (
                    <button
                      key={platform}
                      type="button"
                      onClick={() => togglePlatform(platform)}
                      className={cn(
                        "flex items-center gap-2 px-3 py-2 rounded-lg border transition-all",
                        isSelected
                          ? "border-primary bg-primary/5"
                          : "border-border hover:border-primary/50"
                      )}
                    >
                      <div
                        className="rounded-full p-1.5"
                        style={{ backgroundColor: isSelected ? info.color : info.bgColor }}
                      >
                        <Icon
                          className="h-4 w-4"
                          style={{ color: isSelected ? 'white' : info.color }}
                        />
                      </div>
                      <span className="text-sm font-medium">
                        {account?.account_name || account?.account_username || info.name}
                      </span>
                      {isSelected && (
                        <CheckCircle2 className="h-4 w-4 text-primary" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Content Editor */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Content</Label>
              <div className="flex gap-2">
                {selectedPlatforms.map(platform => {
                  const count = characterCounts[platform];
                  if (!count) return null;
                  return (
                    <Badge
                      key={platform}
                      variant={count.valid ? "secondary" : "destructive"}
                      className="text-xs"
                    >
                      {PLATFORM_INFO[platform].name}: {count.current}/{count.max}
                    </Badge>
                  );
                })}
              </div>
            </div>
            <Textarea
              placeholder="What would you like to share?"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="min-h-[150px] resize-none"
            />
          </div>

          {/* Media Upload */}
          <div className="space-y-3">
            <Label>Media</Label>
            <MediaUploader
              mediaUrls={mediaUrls}
              mediaTypes={mediaTypes}
              onAdd={handleMediaAdd}
              onRemove={handleMediaRemove}
              maxFiles={10}
            />
          </div>

          {/* Platform Validation Messages */}
          {(validation.errors.length > 0 || validation.warnings.length > 0) && (
            <div className="space-y-2">
              {validation.errors.map((error, i) => (
                <div key={`error-${i}`} className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-md">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              ))}
              {validation.warnings.map((warning, i) => (
                <div key={`warning-${i}`} className="flex items-center gap-2 p-3 bg-yellow-50 text-yellow-800 rounded-md">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span className="text-sm">{warning}</span>
                </div>
              ))}
            </div>
          )}

          {/* Schedule */}
          <div className="space-y-3">
            <Label>Schedule</Label>
            <div className="flex flex-wrap gap-3">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[200px] justify-start">
                    <CalendarIcon className="h-4 w-4 mr-2" />
                    {scheduleDate ? format(scheduleDate, 'PPP') : 'Select date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={scheduleDate}
                    onSelect={setScheduleDate}
                    disabled={(date) => date < new Date()}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>

              <Select value={scheduleTime} onValueChange={setScheduleTime}>
                <SelectTrigger className="w-[130px]">
                  <Clock className="h-4 w-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {timeOptions.map(time => (
                    <SelectItem key={time} value={time}>
                      {time}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Preview Button */}
          {selectedPlatforms.length > 0 && content && (
            <div className="flex gap-2">
              {selectedPlatforms.map(platform => (
                <Button
                  key={platform}
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setPreviewPlatform(platform);
                    setShowPreview(true);
                  }}
                >
                  Preview on {PLATFORM_INFO[platform].name}
                </Button>
              ))}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            {onCancel && (
              <Button variant="outline" onClick={onCancel} disabled={isSubmitting}>
                Cancel
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => handleSubmit(false)}
              disabled={isSubmitting || !content.trim() || selectedPlatforms.length === 0 || !scheduleDate || hasValidationErrors}
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Schedule
            </Button>
            <Button
              onClick={() => handleSubmit(true)}
              disabled={isSubmitting || !content.trim() || selectedPlatforms.length === 0 || hasValidationErrors}
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Publish Now
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Preview on {PLATFORM_INFO[previewPlatform].name}
            </DialogTitle>
          </DialogHeader>
          <PostPreview
            platform={previewPlatform}
            content={content}
            mediaUrls={mediaUrls}
            account={activeAccounts.find(a => a.platform === previewPlatform)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PostComposer;
